"use client"
const EditForm = dynamic(() => import("@/app/(user-dashboard)/dashboard/products/create/_components/EditForm"), { ssr: false });
import EditProductModal from '@/app/(user-dashboard)/dashboard/products/create/_components/EditProductModal';
import ProductGallery from '@/app/(user-dashboard)/dashboard/products/create/_components/ProductGallery';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CustomBreadcrumb } from '@/components/utils/CustomBreadcrumb';
import { CircleAlert, LoaderCircleIcon } from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner';
import "./style.scss";
import DynamicSelect from '@/components/utils/DynamicSelect';
import { formatPrice } from '@/lib/utils';
import Icons from '@/components/ui/icons';

const EditProductSection = ({ data }: any) => {
    const [product, setProduct] = useState(data);
    const initialDataRef = useRef({ ...product });
    const [isLoading, setIsLoading] = useState(false)
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const handleUpdateProduct = async () => {
        setIsLoading(true);
        try {
            const formData = new FormData();

            const initialData = JSON.parse(JSON.stringify(product));


            Object.keys(product).forEach((key) => {

                if (JSON.stringify(product[key]) !== JSON.stringify(initialDataRef.current[key])) {
                    if (typeof product[key] === "object" && product[key] !== null) {
                        formData.append(key, JSON.stringify(product[key]));
                    } else {
                        formData.append(key, product[key]);
                    }
                }
            });

            if (product.coverImage instanceof File) {
                formData.append("coverImage", product.coverImage);
            }

            product.images.forEach((image: any) => {
                if (image instanceof File) {
                    formData.append("images", image);
                }
            });

            if (product.file instanceof File) {
                formData.append("file", product.file);
            }

            const response = await fetch(`/api/products/update/${product.id}`, {
                method: "PATCH",
                body: formData,
            });

            if (!response.ok) {
                throw new Error("مشکلی در بروزرسانی محصول پیش آمد.");
            }

            const responseData = await response.json();
            setIsLoading(false);
            toast.success("محصول با موفقیت بروزرسانی شد!");
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.error("خطا در بروزرسانی محصول:", error);
            setIsLoading(false);
            toast.error("خطا در بروزرسانی محصول!");
        }
    };



    const slides = product?.images?.length
        ? product.images.map((image: File | string, index: number) => ({
            id: index + 1,
            title: product?.title,
            image: (typeof window !== "undefined" && image instanceof File)
                ? URL.createObjectURL(image)
                : image,
        }))
        : [
            { id: 1, title: "تخفیف ویژه", image: "/images/assasins.jpg" },
            { id: 2, title: "محصولات جدید", image: "/images/assasins2.jpg" },
            { id: 3, title: "جشنواره پاییزی", image: "/images/assasins3.jpg" },
            { id: 4, title: "افزایش موجودی", image: "/images/assasins4.jpg" },
        ];


    const calculateBasePrice = (options: any[]) => {
        let basePrice = 0;
        options.forEach((option) => {
            const baseValues = option.values.filter((v: any) => v.type === "base");
            if (baseValues.length > 0) {
                const minPrice = Math.min(...baseValues.map((v: any) => v.priceModifier));
                basePrice += minPrice;
            }
        });
        return basePrice;
    };

    const [basePrice, setBasePrice] = useState(calculateBasePrice(product.options));
    const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
    const [finalPrice, setFinalPrice] = useState(basePrice);


    useEffect(() => {
        setBasePrice(calculateBasePrice(product.options));
    }, [product.options]);

    const handleOptionChange = (optionName: string, selectedValue: string) => {
        setSelectedOptions((prev) => ({ ...prev, [optionName]: selectedValue }));
    };

    useEffect(() => {
        let totalPrice = 0;

        Object.entries(selectedOptions).forEach(([optionName, selectedValue]) => {
            const option = product.options.find((opt: any) => opt.name === optionName);
            if (option) {
                const selectedValueObj = option.values.find((val: any) => val.label === selectedValue);
                if (selectedValueObj) {
                    totalPrice += selectedValueObj.priceModifier;
                }
            }
        });

        setFinalPrice(totalPrice);
    }, [selectedOptions, product.options]);



    return (
        <>
            <EditProductModal
                isOpen={isEditModalOpen}
                setIsOpen={setEditModalOpen}
                product={product}
                setProduct={setProduct}
            />
            <div className="fixed bottom-2 p-5 mx-auto inset-x-0 z-50 flex items-center justify-center">
                <div className="flex items-center justify-center gap-3 rounded-full bg-background/5 backdrop-blur-sm p-3">
                    <div className="bg-purple-900/20 rounded-full p-2">
                        <Icons.order className="w-6 h-6" />
                    </div>

                    <div className="flex items-center justify-center gap-2 bg-green-600/20 rounded-full p-2">
                        <Icons.price className="w-6 h-6" />
                        {formatPrice(finalPrice ? finalPrice : product.price)} تومان
                    </div>

                    <div className="bg-pink-900/20 rounded-full p-2">
                        <Icons.heart className="w-6 h-6" />
                    </div>
                </div>
            </div>
            <div className="space-y-6 lg:space-y-16">
                <div className="w-full flex justify-between">
                    <CustomBreadcrumb
                        items={[
                            { name: "داشبورد", path: "/dashboard" },
                            { name: "وبلاگ", path: "/dashboard/products" },
                            { name: "ایجاد پست" }
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button disabled={isLoading} data-loading={isLoading || undefined} variant='secondary' className="text-white bg-[#24ff7f2d] hover:bg-[#24ff7f25]" onClick={handleUpdateProduct}>
                            {isLoading && (
                                <div className="flex items-center justify-center">
                                    <LoaderCircleIcon className="animate-spin" size={16} aria-hidden="true" />
                                </div>
                            )}
                            ذخیره تغییرات
                        </Button>

                        <Button
                            variant="subtle"
                            className="rounded-[10px] bg-[#691ce42c] border-none text-white"
                            onClick={() => setEditModalOpen(true)}
                        >
                            ویرایش اطلاعات پست
                        </Button>

                    </div>

                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 max-w-screen-2xl mx-auto">
                    {/* first section */}
                    <div className="col-span-12 lg:col-span-3 order-2 lg:order-none">
                        <div className="p-2 space-y-2 border border-white/10 bg-[#1A1525]/50 backdrop-blur-lg shadow-lg rounded-2xl sticky top-0">
                            {/* picture */}
                            <div className="h-56 max-h-56 min-h-56 rounded-xl relative overflow-hidden">
                                <Image
                                    src={product?.coverImage
                                        ? (typeof window !== "undefined" && product.coverImage instanceof File
                                            ? URL.createObjectURL(product.coverImage)
                                            : product.coverImage)
                                        : '/images/assasins.jpg'}
                                    alt={product?.title}
                                    fill
                                    style={{ objectFit: 'cover' }}
                                    sizes="100"
                                />


                            </div>

                            {/* another stuff */}
                            <div className="w-full mx-auto px-3 space-y-5 pt-4">
                                {/* seprator */}
                                <div className="w-full border-[0.1px] border-dashed border-[#271D3D] "></div>
                                {/* short descriotion */}
                                <div>
                                    <p className="text-xs text-gray-300">{product?.description} </p>
                                </div>
                                {/* seprator */}
                                <div className="w-full border-[0.1px] border-dashed border-[#271D3D] "></div>
                                {/* product details */}
                                <div className="flex flex-col gap-2">
                                    {/* item 1 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">ژانر:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            {product?.genre.map((value: any) => (
                                                <div key={value + '-item'} className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                    {value}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {/* item 2 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">سال انتشار:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            <div className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                {product?.year}
                                            </div>
                                        </div>
                                    </div>
                                    {/* item 3 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">شرکت سازنده:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            <div className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                {product?.companly}
                                            </div>
                                        </div>
                                    </div>
                                    {/* item 4 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">پلتفرم:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            {[{ id: 0, name: 'استیم' }, { id: 1, name: 'اپیک گیمز' }].map((item) => (
                                                <div key={item.id} className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                    {item.name}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {/* item 5 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">شناسه محصول:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            <div className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                32300
                                            </div>
                                        </div>
                                    </div>
                                    {/* item 6 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">موجودی:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            <div className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                {product?.stock?.quantity}
                                            </div>
                                        </div>
                                    </div>
                                    {/* item 7 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">تحویل بصورت:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            <div className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                {product?.deliveryMethod === 'automatic' ? 'بصورت آنی' : 'تحویل 1 ساعت تا 22 ساعت'}
                                            </div>
                                        </div>
                                    </div>
                                    {/* item 7 */}
                                    <div className="flex justify-between items-start">
                                        <p className="text-sm">تعداد نظرات:</p>
                                        {/* boxs */}
                                        <div className="flex gap-1">
                                            <div className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#44218b2d] shadow border-none text-white">
                                                {product?.reviews?.reviewsCount}
                                            </div>
                                        </div>
                                    </div>
                                    {/* seprator */}
                                    <div className="w-full border-[0.1px] border-dashed border-[#271D3D] "></div>
                                    {/* scores */}
                                    <div className="mx-auto">
                                        <p className="text-sm">بدون امتیاز</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>


                    {/* second section */}
                    <div className="col-span-12 lg:col-span-9 flex flex-col gap-6 order-1 lg:order-none">
                        {/* title */}
                        <div className="flex flex-col gap-3">
                            <div className="flex gap-2">
                                {product?.metadata?.tags.map((value: any) => (
                                    <div key={value + '-item'} className="rounded-[6px] h-[25px] pt-[1px] px-2 flex items-center justify-center text-xs cursor-default bg-[#191620] shadow border-none text-white">
                                        {value}
                                    </div>
                                ))}
                            </div>

                            {/* product title + game name */}
                            <p className="text-2xl">{product?.title}</p>
                            <div className="flex gap-2">
                                {product?.categoryParentName && (
                                    <div className="rounded-[6px] h-7 px-3 flex items-center justify-center text-sm cursor-default bg-[#191620] shadow border-none text-white">
                                        {product?.categoryParentName}
                                    </div>
                                )}

                                {product?.categoryChildName && (
                                    <div className="rounded-[6px] h-7 px-3 flex items-center justify-center text-sm cursor-default bg-[#191620] shadow border-none text-white">
                                        {product?.categoryChildName}
                                    </div>
                                )}

                                <div className="rounded-[6px] h-7 px-3 flex items-center justify-center text-sm cursor-default bg-[#191620] shadow border-none text-white">
                                    {product?.name}
                                </div>
                            </div>

                        </div>
                        {/* first tab */}
                        <div className="relative overflow-hidden space-y-4 pb-4" >
                            <div className="border border-white/10 bg-[#1A1525]/50 backdrop-blur-lg shadow-lg rounded-2xl p-3">
                                <div className="relative overflow-hidden rounded-xl">
                                    <ProductGallery images={slides} />
                                </div>
                            </div>
                            {/* alert */}
                            <div className="flex flex-col gap-3">
                                {product?.options.length > 0 && (
                                    <>
                                        <div className="bg-[#1A1525]/50 backdrop-blur-lg shadow-lg rounded-2xl p-3 grid grid-cols-2 gap-3">
                                            {product.options.map((option: any) => (
                                                <div key={option.name} className="mb-4">
                                                    <p className="mb-2">{option.name}:</p>
                                                    <DynamicSelect
                                                        options={option.values.map((value: any) => ({
                                                            value: value.label,
                                                            label: `${value.label}`,
                                                            description: `${formatPrice(value.priceModifier)} تومان`
                                                        }))}
                                                        label=""
                                                        placeholder={`انتخاب ${option.name}`}
                                                        selectedValue={selectedOptions[option.name]}
                                                        setSelectedValue={(val: any) => handleOptionChange(option.name, val)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {/* <div className="border border-white/10 bg-[#1A1525]/50 backdrop-blur-lg shadow-lg rounded-2xl p-3">
                                    <Alert
                                        layout="complex"
                                        variant="info"
                                        className="rounded-xl"
                                        icon={<CircleAlert className="opacity-60" size={16} strokeWidth={2} />}
                                    >
                                        <AlertContent>
                                            <AlertTitle>توضیحات مرتبط با محصول:</AlertTitle>
                                            <ul className="list-inside list-disc text-sm opacity-80">
                                                <li>آموزش اطلاع از ریجن اکانت استیم</li>
                                                <li>آموزش غیرفعال کردن گارد استیم</li>
                                                <li>آموزش غیرفعال کردن دو مرحله ای اپیک گیمز</li>
                                                <li>خرید سرویس تغییر ریجن اپیک گیمز</li>
                                            </ul>
                                        </AlertContent>
                                    </Alert>
                                </div> */}

                                <EditForm content={product.content} setProduct={setProduct} />
                            </div>
                        </div>

                    </div>
                </div>


                {/* status */}
                {/* <div className="fixed inset-x-0 flex items-end justify-end bottom-3 left-3">
        <div className="border border-white/10 bg-[#1a152565] backdrop-blur-md shadow-lg  rounded-lg p-5 ">

        </div>
      </div> */}
            </div>
        </>
    )
}

export default EditProductSection